import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Grid, Button, Tabs, Tab, Divider } from '@mui/material';
import { Page, Header } from 'components';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SettingsIcon from '@mui/icons-material/Settings';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import Description from '@mui/icons-material/Description';
import PostAdd from '@mui/icons-material/PostAdd';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import BridgeCallDatatable from './components/BridgeCallDatatable';
import BridgeCallCreationDialog from './components/BridgeCallCreationDialog/BridgeCallCreationDialog';

import {
	SetFormState, GetTasks, GetTasksDropdowns,
	ExportBridges, ToggleCreationBridgeCallDialog,
	DownloadTemplate, UploadStatusFile,
	GetBridgeStatus, CloseBridge
} from '../actions/BridgeCallActions';
import { Close } from '@mui/icons-material';
import BridgeCallStatus from './components/BridgeCallStatus/BridgeCallStatus';
import useCustomURLParams from 'hooks/useCustomURLParams';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(1),
	},
	tabs: {
		marginTop: theme.spacing(1),
	},
	content: {
		marginTop: theme.spacing(1),
	},
}));

const BridgeCall = () => {
	const classes = useStyles();

	const [queryObject, setQueryObject] = useCustomURLParams();

	const dispatch = useDispatch();
	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);
	const tasks = useSelector((state) => state.bridgeCallReducer.tasks);
	const creationBridgeCallDialogVisibility = useSelector((state) => state.bridgeCallReducer.creationBridgeCallDialogVisibility);

	const tableName = 'BridgeCallTable';
	

	useEffect(() => {
		dispatch(GetTasks(userId));
		dispatch(GetTasksDropdowns());
	}, []);

	const _optionsBridgeCallDialogClose = () => {
		dispatch(ToggleCreationBridgeCallDialog(false));
	};

	const _onExportBridgesClick = () => {
		dispatch(ExportBridges(userId));
	};

	const _onDownloadTemplate = () => {
		dispatch(DownloadTemplate(userId));
	};

	const _uploadFiles = (file) => {
		dispatch(UploadStatusFile(userId, file));
	};

	const _onCreationClick = () => {
		dispatch(ToggleCreationBridgeCallDialog(true));
	};
	const _onCloseClick = () => {
		dispatch(CloseBridge(userId));
	};

	const _onStatusClick = () => {
		dispatch(GetBridgeStatus(userId));
	};

	const actions = [
		{ icon: <FileOpenIcon sx={{ color: 'blue' }} />, name: 'Export Tasks', id: 'ExportBridges' },
		{ icon: <Description sx={{ color: 'orange' }} />, name: 'Download Template', id: 'dlTemplate' },
		{ icon: <PostAdd sx={{ color: 'purple' }} />, name: 'Upload Status File', id: 'ulStatus' },
	];

	if (role === 'Administrator' || role === 'Manager') {
		actions.push({ icon: <PersonAddAltIcon sx={{ color: 'green' }} />, name: 'Create New Bridge', id: 'createNewBridge' });
	}

	function UploadSpeedDialAction(props) {
		return (
			<React.Fragment>
			<input
				accept=".zip"
				style={{ display: "none" }}
				id="icon-button-file"
				type="file"
				onChange={(e) => _uploadFiles(e.target.files[0])}
			/>
			<label htmlFor="icon-button-file">
				<SpeedDialAction
					icon={props.icon}
					tooltipTitle="upload"
					component="span"
					{...props}
				></SpeedDialAction>
			</label>
			</React.Fragment>
		);
	}

	const _handleSpeedDialClick = (action) => {

		if (action.id === 'ExportBridges') {
			_onExportBridgesClick();
		} else if (action.id === 'createNewBridge') {
			_onCreationClick();
		} else if (action.id === 'dlTemplate') {
			_onDownloadTemplate();
		}
	};

	const tabs = [
		{ value: 'vodafone', label: 'Vodafone' },
		{ value: 'three', label: 'Three' },
		{ value: 'vmo2', label: 'VMO2' },
		{ value: 'status', label: 'Status' }
	];

	const getTabName = () => {
		const validTabs = tabs.map(t => t.value);
		const tab = queryObject["tab"];
		if (validTabs.includes(tab)) { return tab; }
		return 'vodafone';
	};

	const [tab, setTab] = useState(getTabName());

	const handleTabsChange = (event, value) => {
		setCurrentTab(event, value);
	};

	const setCurrentTab = (_, newValue) => {
		// update the query param
		setQueryObject({ tab: newValue });
		setTab(newValue);
	};

	return (
		<Page className={classes.root} title="Bridge Call">
			<Header module="Modules"
				view={
					<>
						Bridge Call:{' | '}
						<span style={{
							fontSize: '1.1rem',
							color: tasks.lastBridgeStatus === 'OPEN' ? 'green' : 'red'
						}}>{tasks.lastBridgeStatus}</span>
						{' | '}
						<span style={{ fontSize: '1rem' }}>{tasks.lastCreationInfo}</span>
					</>
				}
			/>

			<SpeedDial
				ariaLabel="SpeedDial"
				sx={{ position: 'absolute', bottom: 16, right: 16 }}
				icon={<SettingsIcon />}
			>
				{actions.map((action) => (
					action.id === 'ulStatus' ? (
						<UploadSpeedDialAction
							key={action.name}
							tooltipTitle={action.name}
							icon={action.icon}
						/>
					) : (<SpeedDialAction
						key={action.name}
						icon={action.icon}
						tooltipTitle={action.name}
						onClick={() => { _handleSpeedDialClick(action); }}
					/>)
				))}
			</SpeedDial>
			<Grid className={classes.container} container spacing={1}>
				<Grid item lg={12} xs={12}>
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Tabs
							className={classes.tabs}
							onChange={handleTabsChange}
							scrollButtons="auto"
							value={tab}
							variant="scrollable"
						>
							{tabs.map((t) => (
								<Tab key={t.value} label={t.label} value={t.value} />
							))}
						</Tabs>
						<div style={{ marginRight: '20px', gap: '10px', display: 'flex', alignItems: 'center' }}>
							<Button variant="outlined" color="secondary" onClick={_onStatusClick} style={{ height: '100%' }}>
								<Description style={{ marginRight: '5px' }} />
								Download Status
							</Button>
							{tasks.lastBridgeStatus === 'OPEN' ? (
								<Button variant="outlined" color="error" onClick={_onCloseClick} style={{ height: '100%' }}>
									<Close style={{ marginRight: '5px' }} />
									Close Bridge
								</Button>
							) : (<></>)}
						</div>
						
					</div>

					<Divider className={classes.divider} />
					<div className={classes.content}>
						{tab === 'vodafone'
							&&
							<BridgeCallDatatable className={classes.results} data={tasks.bridgeCallVodafone} tableName={tableName} bridgeStatus={tasks.lastBridgeStatus} disabled={tasks.lastBridgeStatus !== 'OPEN'} />}
						{tab === 'three'
							&&
							<BridgeCallDatatable className={classes.results} data={tasks.bridgeCallThree} tableName={tableName} bridgeStatus={tasks.lastBridgeStatus} disabled={tasks.lastBridgeStatus !== 'OPEN'} />}
						{tab === 'vmo2'
							&&
							<BridgeCallDatatable className={classes.results} data={tasks.bridgeCallVMO2} tableName={tableName} bridgeStatus={tasks.lastBridgeStatus} disabled={tasks.lastBridgeStatus !== 'OPEN'} />}
						{tab === 'status'
							&&
							<BridgeCallStatus />}
					</div>
					{/* <BridgeCallDatatable
						className={classes.results}
						data={tasks}
						tableName={tableName}
					/> */}
					{creationBridgeCallDialogVisibility && (
						<BridgeCallCreationDialog
							onClose={_optionsBridgeCallDialogClose}
							open={creationBridgeCallDialogVisibility}
						/>
					)}
				</Grid>
			</Grid>
		</Page >
	);
};

export default BridgeCall;
